import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';

import { ApiError } from '../api/client';
import { getPublicInvoice } from '../api/endpoints/invoices';
import { EmptyState } from '../components/EmptyState';
import { FacturaSheet } from '../components/FacturaSheet';
import { LoadingState } from '../components/LoadingState';

/**
 * La factura como la abre el cliente. NO SESSION, no plan, no negocio: the link
 * is the whole of it, which is why this page hangs off the root route with no
 * `RequireAuth` around it and reads a public endpoint of its own.
 *
 * It renders the SAME `FacturaSheet` the owner's print view does, so the
 * customer's printout and the counter's printout are the same document.
 * Everything the panel wraps around that sheet (the toolbar back to /facturas,
 * the anular controls) is deliberately absent: there is nothing here for a
 * customer to do except read the document and print it.
 */
export function FacturaPublicaPage() {
  const { shareToken } = useParams({ from: '/factura/$shareToken' });
  const facturaQuery = useQuery({
    queryKey: ['public-invoice', shareToken],
    queryFn: () => getPublicInvoice(shareToken),
    retry: false,
  });

  if (facturaQuery.isLoading) {
    return (
      <main>
        <LoadingState label="Cargando factura…" />
      </main>
    );
  }

  if (facturaQuery.isError || !facturaQuery.data) {
    // One answer for every reason: an expired link, a typo and a token that
    // never existed all read the same, so this page tells a guesser nothing.
    return (
      <main>
        <EmptyState
          title="No encontramos esa factura."
          description={errorMessage(facturaQuery.error)}
        />
      </main>
    );
  }

  const factura = facturaQuery.data;

  return (
    <main>
      <div className="factura-toolbar">
        <button type="button" className="btn small" onClick={() => window.print()}>
          Imprimir
        </button>
      </div>

      <FacturaSheet
        factura={{
          emisor: factura.emisor,
          displayNumber: factura.displayNumber,
          status: factura.status,
          issuedAt: factura.issuedAt,
          clientName: factura.clientName,
          clientRuc: factura.clientRuc,
          clientDv: factura.clientDv,
          subtotalCents: factura.subtotalCents,
          discountCents: factura.discountCents,
          itbmsRateBp: factura.itbmsRateBp,
          itbmsCents: factura.itbmsCents,
          totalCents: factura.totalCents,
          voidReason: factura.voidReason,
          lines: factura.items,
        }}
      />
    </main>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 404) {
    return 'Revisa el enlace que te enviaron, o pídelo de nuevo al negocio.';
  }
  return 'Prueba de nuevo en un momento.';
}
